import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  X,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Ruler,
  CheckCircle,
  Eye
} from 'lucide-react';
import { CATEGORIES, PROJECT_META, ProjectMeta } from '../../data/projects';
import { useTheme } from '../../context/ThemeContext';

export const ONGOING_SITES = [
  { src: "/images/ongoing/hyderabad/hyd-1.jpg", locality: "Kokapet, Hyd", stage: "Electrical & Ceiling Wiring", details: "Routing ceiling electrical wiring and conduits for custom spotlight fixtures." },
  { src: "/images/ongoing/vijayawada/vij-1.jpg", locality: "Benz Circle, Vja", stage: "Open Crockery Cabinet", details: "Installing tall open shelving cabinet with marble-pattern backing sheets in the dining area." },
  { src: "/images/ongoing/hyderabad/hyd-2.jpg", locality: "Kokapet, Hyd", stage: "Civil Design Layout", details: "Taping CAD blueprints to raw brick wall for structural markings and wall grooving." },
  { src: "/images/ongoing/vijayawada/vij-2.jpg", locality: "Benz Circle, Vja", stage: "TV Entertainment Console", details: "Assembling living room television console panels and low cabinets." },
  { src: "/images/ongoing/hyderabad/hyd-3.jpg", locality: "Kokapet, Hyd", stage: "Modular Wardrobe Frame", details: "Assembling bedroom wardrobe carcass frame compartments using BWR plywood." },
  { src: "/images/ongoing/vijayawada/vij-3.jpg", locality: "Benz Circle, Vja", stage: "Dining Side Credenza", details: "Fitting base cabinet carcasses made of SAINIK 710 BWR plywood with open niches." },
  { src: "/images/ongoing/hyderabad/hyd-4.jpg", locality: "Kokapet, Hyd", stage: "Kitchen Wall Cabinets", details: "Fitting modular overhead cabinets with clean wood laminates above the counter space." },
  { src: "/images/ongoing/vijayawada/vij-4.jpg", locality: "Benz Circle, Vja", stage: "Modular Pantry & Cabinets", details: "Aligning L-shape modular kitchen wall cabinets and a tall appliance pantry tower." },
  { src: "/images/ongoing/hyderabad/hyd-5.jpg", locality: "Kokapet, Hyd", stage: "Living Room TV Wall", details: "Installing structural plywood backboards for a full-height TV media panel." },
  { src: "/images/ongoing/vijayawada/vij-5.jpg", locality: "Benz Circle, Vja", stage: "Wardrobe & Study Desk", details: "Constructing bedroom wardrobe carcass shelves and an integrated window study counter." },
  { src: "/images/ongoing/hyderabad/hyd-6.jpg", locality: "Kokapet, Hyd", stage: "Kitchen Base Cabinets", details: "Aligning soft-close drawer units and preparing modular kitchen carcass sections." }
];

export function ProjectsSection() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // State Management
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeProjectSlug, setActiveProjectSlug] = useState<string>("mummaneni-residency");
  const [ongoingLightboxIndex, setOngoingLightboxIndex] = useState<number | null>(null);
  const [localityFilter, setLocalityFilter] = useState<'all' | 'hyderabad' | 'vijayawada' | 'bangalore'>('all');

  // Reset locality sub-filter when main category changes
  useEffect(() => {
    setLocalityFilter('all');
  }, [activeCategory]);

  // Filtered ongoing sites list based on selected locality
  const filteredOngoingSites = React.useMemo(() => {
    if (localityFilter === 'all') return ONGOING_SITES;
    if (localityFilter === 'hyderabad') {
      return ONGOING_SITES.filter(site => site.src.includes('/hyderabad/'));
    }
    if (localityFilter === 'vijayawada') {
      return ONGOING_SITES.filter(site => site.src.includes('/vijayawada/'));
    }
    return ONGOING_SITES;
  }, [localityFilter]);

  // Keydown navigation for ongoing sites lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (ongoingLightboxIndex === null) return;
      if (e.key === 'ArrowRight') {
        setOngoingLightboxIndex((prev) => (prev !== null ? (prev + 1) % filteredOngoingSites.length : null));
      }
      if (e.key === 'ArrowLeft') {
        setOngoingLightboxIndex((prev) => (prev !== null ? (prev - 1 + filteredOngoingSites.length) % filteredOngoingSites.length : null));
      }
      if (e.key === 'Escape') {
        setOngoingLightboxIndex(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ongoingLightboxIndex, filteredOngoingSites]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Retrieve projects based on Category Filter and Locality Filter
  const filteredProjects = React.useMemo(() => {
    const allowedSlugs = CATEGORIES.flatMap(c => c.projectSlugs);
    let projects = [];

    if (activeCategory === "all") {
      projects = allowedSlugs
        .map(slug => ({ slug, ...PROJECT_META[slug] }))
        .filter(p => p && p.title);
    } else {
      const catDef = CATEGORIES.find(c => c.id === activeCategory);
      if (catDef) {
        projects = catDef.projectSlugs
          .map(slug => ({ slug, ...PROJECT_META[slug] }))
          .filter(p => p && p.title);
      }
    }

    // Apply locality sub-filter for main projects
    if (localityFilter !== 'all') {
      projects = projects.filter(p => {
        const loc = p.location.toLowerCase();
        if (localityFilter === 'hyderabad') {
          return loc.includes('hyderabad') || loc.includes('gachibowli') || loc.includes('kokapet') || loc.includes('narsingi') || loc.includes('madhapur') || loc.includes('kondapur') || loc.includes('kphb') || loc.includes('nanakramguda') || loc.includes('hafeezpet');
        }
        if (localityFilter === 'vijayawada') {
          return loc.includes('vijayawada') || loc.includes('benz circle') || loc.includes('nidamanuru') || loc.includes('vja');
        }
        if (localityFilter === 'bangalore') {
          return loc.includes('bengaluru') || loc.includes('bangalore') || loc.includes('whitefield') || loc.includes('sarjapur');
        }
        return true;
      });
    }

    return projects;
  }, [activeCategory, localityFilter]);

  // Adjust active project slug if the active category changes
  useEffect(() => {
    if (filteredProjects.length > 0) {
      // Find if current slug is in the filtered list
      const exists = filteredProjects.some(p => p.slug === activeProjectSlug);
      if (!exists) {
        // Fallback to the first project in the new category
        setActiveProjectSlug(filteredProjects[0].slug);
      }
    }
  }, [filteredProjects, activeProjectSlug]);

  // Ref to the active project chip button
  const activeChipRef = React.useRef<HTMLButtonElement | null>(null);

  // Ref to the horizontal scroll track container
  const scrollTrackRef = React.useRef<HTMLDivElement | null>(null);

  // Auto-scroll the active project chip into view inside its container when slug changes
  useEffect(() => {
    if (activeChipRef.current && scrollTrackRef.current) {
      const container = scrollTrackRef.current;
      const chip = activeChipRef.current;

      const containerRect = container.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();

      const targetScrollLeft =
        container.scrollLeft +
        (chipRect.left - containerRect.left) -
        (containerRect.width / 2) +
        (chipRect.width / 2);

      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeProjectSlug]);

  // Auto-play projects in a fixed interval of 3.5 seconds (resets on manual selection)
  useEffect(() => {
    if (filteredProjects.length <= 1 || lightboxIndex !== null || isHovered) {
      return;
    }

    const intervalId = setInterval(() => {
      const currentIndex = filteredProjects.findIndex(p => p.slug === activeProjectSlug);
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % filteredProjects.length;
      setActiveProjectSlug(filteredProjects[nextIndex].slug);
    }, 7500);

    return () => clearInterval(intervalId);
  }, [filteredProjects, activeProjectSlug, lightboxIndex, isHovered]);

  // Active Project Data Details
  const activeProject = PROJECT_META[activeProjectSlug] || Object.values(PROJECT_META)[0];

  // Default specifications if missing in data (for safety)
  const defaultSpecs = [
    "High-grade plywood understructure",
    "Bespoke layout configuration",
    "Premium lacquer/acrylic textures",
    "Ambient profile lighting integrations"
  ];
  const specsToUse = activeProject.specs && activeProject.specs.length > 0 ? activeProject.specs : defaultSpecs;

  // Lightbox Handlers
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const nextImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (lightboxIndex !== null && activeProject.gallery.length > 0) {
      setLightboxIndex((lightboxIndex + 1) % activeProject.gallery.length);
    }
  };

  const prevImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (lightboxIndex !== null && activeProject.gallery.length > 0) {
      setLightboxIndex(
        (lightboxIndex - 1 + activeProject.gallery.length) % activeProject.gallery.length
      );
    }
  };

  // Keyboard Navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex]);

  // Active showcase images and length calculation
  const activeImages = activeProject.gallery.slice(0, 5);
  const galleryLength = activeImages.length;

  // Determine grid container class dynamically based on number of active images, stretching to full height to align with details
  const getGridContainerClass = () => {
    if (galleryLength === 1) {
      return "grid grid-cols-1 grid-rows-1 gap-3.5 h-full min-h-[300px] md:min-h-full";
    }
    if (galleryLength === 2) {
      return "grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-3.5 h-full min-h-[340px] md:min-h-full";
    }
    if (galleryLength === 3) {
      return "grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-3.5 h-full min-h-[340px] md:min-h-full";
    }
    if (galleryLength === 4) {
      return "grid grid-cols-2 grid-rows-3 gap-3.5 h-full min-h-[380px] md:min-h-full";
    }
    // Default (5 or more images)
    return "grid grid-cols-2 md:grid-cols-3 grid-rows-3 gap-3.5 h-full min-h-[400px] md:min-h-full";
  };

  // Determine individual cell classes dynamically based on active gallery size to avoid layout gaps
  const getDynamicCellClass = (index: number) => {
    if (galleryLength === 1) {
      return "col-span-1 row-span-1 h-full w-full";
    }

    if (galleryLength === 2) {
      if (index === 0) return "col-span-1 md:col-span-2 row-span-1 md:row-span-2 h-full w-full";
      return "col-span-1 md:col-span-1 row-span-1 md:row-span-2 h-full w-full";
    }

    if (galleryLength === 3) {
      if (index === 0) return "col-span-1 md:col-span-2 row-span-1 md:row-span-2 h-full w-full";
      if (index === 1) return "col-span-1 md:col-span-1 row-span-1 md:row-span-1 h-full w-full";
      return "col-span-1 md:col-span-1 row-span-1 md:row-span-1 h-full w-full";
    }

    if (galleryLength === 4) {
      if (index === 0) return "col-span-1 row-span-2 h-full w-full"; // Tall left (Col 1, Row 1-2)
      if (index === 1) return "col-span-1 row-span-1 h-full w-full"; // Square top-right (Col 2, Row 1)
      if (index === 2) return "col-span-1 row-span-1 h-full w-full"; // Square bottom-right (Col 2, Row 2)
      return "col-span-2 row-span-1 h-full w-full"; // Full-width wide bottom (Col 1-2, Row 3)
    }

    // Default 5 images puzzle grid
    switch (index) {
      case 0:
        return "col-span-2 md:col-span-2 row-span-1 md:row-span-2 h-full w-full"; // Main (2x2 cells)
      case 1:
        return "col-span-1 md:col-span-1 row-span-1 md:row-span-1 h-full w-full"; // Top-Right (1x1 cell)
      case 2:
        return "col-span-1 md:col-span-1 row-span-1 md:row-span-2 h-full w-full"; // Right-Column Tall (1x2 cells)
      case 3:
        return "col-span-1 md:col-span-1 row-span-1 md:row-span-1 h-full w-full"; // Bottom-Left (1x1 cell)
      case 4:
        return "col-span-1 md:col-span-1 row-span-1 md:row-span-1 h-full w-full"; // Bottom-Center (1x1 cell)
      default:
        return "col-span-1 row-span-1 h-full w-full";
    }
  };

  return (
    <section id="projects" className={`py-20 sm:py-28 transition-colors duration-500 relative overflow-hidden ${isDark ? 'bg-dark-bg' : 'bg-bg-light'}`}>

      {/* Dynamic Background Gradients */}
      <div className="absolute top-1/3 -right-48 w-[450px] h-[450px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 -left-48 w-[450px] h-[450px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 mb-4"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent">OUR PORTFOLIO</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-3xl sm:text-4xl lg:text-[42px] font-poppins font-bold leading-tight mb-4 tracking-tight ${isDark ? 'text-white' : 'text-primary'}`}
          >
            Our Real Projects.{' '}
            <span className="text-accent">Exquisite Finished Spaces.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-base sm:text-lg max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          >
            Explore our curated portfolio of beautiful homes, commercial spaces, and custom work.
          </motion.p>
        </div>

        {/* Level 1: Category Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12">
          {/* "All" Option */}
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-3 py-1.5 md:px-4.5 md:py-2.5 rounded-full text-[10px] md:text-xs uppercase font-poppins tracking-wider font-bold transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${activeCategory === "all"
              ? 'bg-accent text-white shadow-md shadow-accent/20 border border-accent'
              : isDark
                ? 'bg-[#121A2E] text-gray-400 hover:text-white border border-white/5'
                : 'bg-white text-gray-600 hover:text-primary border border-gray-100 shadow-xs'
              }`}
          >
            All Projects
          </button>

          {/* Ongoing Sites Category */}
          <button
            onClick={() => setActiveCategory("ongoing")}
            className={`px-3 py-1.5 md:px-4.5 md:py-2.5 rounded-full text-[10px] md:text-xs uppercase font-poppins tracking-wider font-bold transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${activeCategory === "ongoing"
              ? 'bg-accent text-white shadow-md shadow-accent/20 border border-accent'
              : isDark
                ? 'bg-[#121A2E] text-gray-400 hover:text-white border border-white/5'
                : 'bg-white text-gray-600 hover:text-primary border border-gray-100 shadow-xs'
              }`}
          >
            Ongoing Sites
          </button>

          {/* Individual Categories */}
          {CATEGORIES.filter((cat) => cat.projectSlugs.length > 0).map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3 py-1.5 md:px-4.5 md:py-2.5 rounded-full text-[10px] md:text-xs uppercase font-poppins tracking-wider font-bold transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${activeCategory === cat.id
                ? 'bg-accent text-white shadow-md shadow-accent/20 border border-accent'
                : isDark
                  ? 'bg-[#121A2E] text-gray-400 hover:text-white border border-white/5'
                  : 'bg-white text-gray-600 hover:text-primary border border-gray-100 shadow-xs'
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Level 2 Subcategory: Locality Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12">
          {(activeCategory === "ongoing"
            ? [
              { id: 'all', label: 'All Locations' },
              { id: 'hyderabad', label: 'Hyderabad' },
              { id: 'vijayawada', label: 'Vijayawada' }
            ]
            : [
              { id: 'all', label: 'All Locations' },
              { id: 'hyderabad', label: 'Hyderabad' },
              { id: 'vijayawada', label: 'Vijayawada' },
              { id: 'bangalore', label: 'Bangalore' }
            ]
          ).map((loc) => (
            <button
              key={loc.id}
              onClick={() => {
                setLocalityFilter(loc.id as any);
                setOngoingLightboxIndex(null); // Reset lightbox index when filter changes
              }}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-[10px] md:text-xs uppercase font-poppins tracking-wider font-extrabold transition-all duration-300 transform hover:scale-[1.01] cursor-pointer ${localityFilter === loc.id
                ? isDark
                  ? 'bg-accent/20 border border-accent text-white shadow-lg shadow-accent/10'
                  : 'bg-accent/10 border border-accent text-accent shadow-md shadow-accent/5'
                : isDark
                  ? 'bg-[#0E1626] border border-white/[0.04] text-gray-400 hover:text-white hover:border-white/10'
                  : 'bg-white border border-black/[0.03] text-gray-600 hover:text-primary hover:border-accent/20 shadow-xs'
                }`}
            >
              {loc.label}
            </button>
          ))}
        </div>

        {/* Level 2: Horizontal Scrollable Project Chips Track */}
        {activeCategory !== "ongoing" && (
          <div className="relative mb-8 sm:mb-16">
            {/* Subtle edge fade overlays to hint scroller direction */}
            <div className={`absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r ${isDark ? 'from-dark-bg' : 'from-bg-light'} to-transparent pointer-events-none z-10 opacity-0 md:group-hover:opacity-100 transition-opacity`} />
            <div className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l ${isDark ? 'from-[#0B0F1A]' : 'from-bg-light'} to-transparent pointer-events-none z-10`} />

            {/* Scroll Track container with completely hidden scrollbar */}
            <div ref={scrollTrackRef} className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
              {filteredProjects.map((proj) => {
                const isActive = activeProjectSlug === proj.slug;
                return (
                  <button
                    key={proj.slug}
                    ref={isActive ? activeChipRef : null}
                    onClick={() => setActiveProjectSlug(proj.slug)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 md:px-4.5 md:py-3.5 rounded-xl md:rounded-2xl whitespace-nowrap text-[10px] md:text-xs uppercase font-poppins tracking-wider font-extrabold transition-all duration-300 transform hover:translate-y-[-1px] cursor-pointer relative shrink-0 ${isActive
                      ? isDark
                        ? 'bg-accent/15 border-2 border-accent text-white shadow-lg shadow-accent/10'
                        : 'bg-accent/10 border-2 border-accent text-accent shadow-md shadow-accent/5'
                      : isDark
                        ? 'bg-[#0E1626] border border-white/[0.04] text-gray-400 hover:text-white hover:border-white/10'
                        : 'bg-white border border-black/[0.03] text-gray-600 hover:text-primary hover:border-accent/20 shadow-xs'
                      }`}
                  >
                    {isActive && <span className="w-1 h-1 rounded-full bg-accent animate-ping" />}
                    {proj.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Cinematic Split-Screen Layout */}
        {activeCategory === "ongoing" ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12">
            {filteredOngoingSites.map((proj, idx) => (
              <div
                key={idx}
                onClick={() => setOngoingLightboxIndex(idx)}
                className={`group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer border ${isDark ? 'border-white/[0.04] bg-[#0E1626]' : 'border-slate-200/50 bg-slate-105'
                  }`}
              >
                <img
                  src={proj.src}
                  alt={`${proj.stage} in ${proj.locality}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-85 md:opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
                  <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent mb-0.5">
                    {proj.locality}
                  </span>
                  <strong className="text-xs sm:text-sm font-semibold truncate">
                    {proj.stage}
                  </strong>
                  <p className="text-[10px] sm:text-xs text-gray-300 line-clamp-1 mt-1 font-light">
                    {proj.details}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className={`p-12 text-center rounded-3xl border mt-8 ${isDark ? 'bg-[#111827]/40 border-white/[0.04]' : 'bg-white border-slate-200/50 shadow-sm'}`}>
            <p className={`font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              No projects showcased in this location/category yet. Contact us to see more.
            </p>
          </div>
        ) : (
          <div
            className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >

            {/* Left Column: Project Information Details Card */}
            <div className="lg:col-span-4 flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeProjectSlug}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.4 }}
                  className={`rounded-3xl p-6 sm:p-8 border flex flex-col justify-between h-full min-h-[380px] sm:min-h-[440px] relative overflow-hidden ${isDark
                    ? 'bg-[#111827]/85 border-white/[0.06] shadow-xl'
                    : 'bg-white border-black/[0.04] shadow-xl shadow-slate-100/50'
                    }`}
                >
                  <div>
                    {/* Category & Location Badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold px-2.5 py-1 bg-accent/10 text-accent rounded-md border border-accent/20">
                        {activeProject.category}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <MapPin className="w-3.5 h-3.5 text-accent shrink-0" />
                        {activeProject.location}
                      </span>
                    </div>

                    {/* Project Main Heading */}
                    <h3 className={`text-2xl sm:text-3xl lg:text-4xl font-poppins font-extrabold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
                      {activeProject.title}
                    </h3>

                    {/* Area Specification Badge */}
                    <div className={`inline-flex items-center gap-1.5 text-xs lg:text-sm font-poppins font-bold px-3 py-1.5 rounded-lg border mb-5 ${isDark
                      ? 'bg-white/5 border-white/10 text-gray-300'
                      : 'bg-slate-100 border-slate-200/50 text-gray-600'
                      }`}>
                      <Ruler className="w-4 h-4 text-accent shrink-0" />
                      Area: {activeProject.area}
                    </div>

                    {/* Short Summary Description */}
                    <p className={`text-sm lg:text-base leading-relaxed mb-6 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {activeProject.description}
                    </p>

                    {/* Dynamic Custom Specifications List */}
                    <div>
                      <h4 className={`text-[10px] lg:text-[11px] uppercase font-poppins tracking-wider font-extrabold mb-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        COMPLETED SPECIFICATIONS
                      </h4>
                      <ul className="space-y-2.5">
                        {specsToUse.map((spec) => (
                          <li key={spec} className="flex items-start gap-2 text-xs lg:text-sm font-semibold leading-snug">
                            <CheckCircle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>

                  {/* Footer Route CTA */}
                  <div className="pt-6 mt-8 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => navigate(`/case-study/${activeProjectSlug}`)}
                      className="inline-flex items-center gap-2 text-xs lg:text-sm uppercase font-poppins tracking-wider font-black text-accent hover:text-[#B58A54] group cursor-pointer transition-all duration-300"
                    >
                      View Case Study
                      <ArrowRight className="w-4 h-4 text-accent transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>

                  {/* Autoplay Progress Bar Indicator */}
                  {filteredProjects.length > 1 && lightboxIndex === null && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent/15 overflow-hidden">
                      <div
                        key={activeProjectSlug}
                        className="h-full bg-accent animate-progress origin-left"
                        style={{
                          animationPlayState: isHovered ? 'paused' : 'running'
                        }}
                      />
                    </div>
                  )}

                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right Column: Asymmetrical Photo Mosaic Grid */}
            <div className="lg:col-span-8 flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeProjectSlug}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.45 }}
                  className={getGridContainerClass()}
                >
                  {activeImages.map((image, idx) => {
                    const isLast = idx === galleryLength - 1;
                    const hasMore = activeProject.gallery.length > galleryLength;

                    return (
                      <button
                        key={image.src}
                        onClick={() => openLightbox(idx)}
                        className={`group relative rounded-2xl overflow-hidden cursor-pointer border hover:border-accent/30 transition-all duration-300 shadow-md ${getDynamicCellClass(idx)} ${isDark ? 'border-white/[0.04]' : 'border-black/[0.03]'
                          }`}
                      >
                        {/* Background Blur Backlight Effect */}
                        <div className="absolute inset-0 bg-accent/20 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 blur-xl transition-all duration-500 pointer-events-none" />

                        {/* Image Asset */}
                        <img
                          src={image.src}
                          alt={`${activeProject.title} - ${image.tag}`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                        />

                        {/* Glassmorphic Overlay Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

                        {/* Tag Label */}
                        {(!isLast || !hasMore) && (
                          <div className="absolute left-3.5 bottom-3.5 z-10 pointer-events-none">
                            <span className="text-[10px] md:text-xs font-poppins font-bold px-2.5 py-1 bg-black/60 backdrop-blur-md border border-white/15 text-white/90 rounded-md tracking-wide shadow-xs">
                              {image.tag}
                            </span>
                          </div>
                        )}

                        {/* Click Indicator Zoom Button / "+ More" overlay */}
                        {isLast && hasMore ? (
                          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs flex flex-col items-center justify-center z-15 group-hover:bg-black/50 transition-colors">
                            <Eye className="w-5 h-5 text-accent mb-1 animate-pulse" />
                            <span className="text-white font-poppins text-[15px] md:text-lg font-black tracking-wider">
                              + {activeProject.gallery.length - 4} MORE
                            </span>
                            <span className="text-white/60 text-[9px] md:text-[10px] uppercase font-poppins tracking-widest font-bold mt-0.5">
                              View Gallery
                            </span>
                          </div>
                        ) : (
                          <div className="absolute right-3.5 bottom-3.5 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center translate-y-2 opacity-0 scale-90 group-hover:translate-y-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-350 shadow-md">
                            <Maximize2 className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}

                      </button>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </div>

          </div>
        )}

      </div>

      {/* Built-In Premium Lightbox Overlay Portal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-2000 bg-black/95 backdrop-blur-md flex items-center justify-center select-none"
            onClick={closeLightbox}
          >
            {/* Header info */}
            <div className="absolute top-0 left-0 right-0 p-5 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent">
              <div className="text-left">
                <h4 className="text-white font-poppins font-extrabold text-base tracking-tight">
                  {activeProject.title}
                </h4>
                <p className="text-white/60 text-xs font-poppins font-bold uppercase tracking-wider mt-0.5">
                  Category: {activeProject.category} • Tag: {activeProject.gallery[lightboxIndex].tag}
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-colors text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Previous Image Trigger */}
            <button
              onClick={prevImage}
              className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Central High-Res Showcase Image */}
            <div className="max-w-[85vw] max-h-[75vh] flex items-center justify-center p-2 select-none relative" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={lightboxIndex}
                  src={activeProject.gallery[lightboxIndex].src}
                  alt={`${activeProject.title} - Highlight`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl border border-white/5"
                />
              </AnimatePresence>
            </div>

            {/* Next Image Trigger */}
            <button
              onClick={nextImage}
              className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dynamic Bottom Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md border border-white/15 px-4 py-1.5 rounded-full text-xs font-poppins font-extrabold tracking-widest text-white shadow-md">
              {lightboxIndex + 1} OF {activeProject.gallery.length} IMAGES
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* Built-In Premium Lightbox Overlay Portal for Ongoing Sites */}
      <AnimatePresence>
        {ongoingLightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-2000 bg-black/95 backdrop-blur-md flex items-center justify-center select-none"
            onClick={() => setOngoingLightboxIndex(null)}
          >
            {/* Header info */}
            <div className="absolute top-0 left-0 right-0 p-5 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent text-left">
              <div>
                <h4 className="text-white font-poppins font-extrabold text-base tracking-tight">
                  {filteredOngoingSites[ongoingLightboxIndex].stage}
                </h4>
                <p className="text-white/60 text-xs font-poppins font-bold uppercase tracking-wider mt-0.5">
                  Locality: {filteredOngoingSites[ongoingLightboxIndex].locality}
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setOngoingLightboxIndex(null)}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-colors text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Previous Image Trigger */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOngoingLightboxIndex((ongoingLightboxIndex - 1 + filteredOngoingSites.length) % filteredOngoingSites.length);
              }}
              className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Central Image */}
            <div className="max-w-[85vw] max-h-[70vh] flex flex-col items-center gap-4 select-none relative" onClick={(e) => e.stopPropagation()}>
              <img
                src={filteredOngoingSites[ongoingLightboxIndex].src}
                alt={filteredOngoingSites[ongoingLightboxIndex].stage}
                className="max-w-full max-h-[65vh] object-contain rounded-2xl shadow-2xl border border-white/5"
              />
              <p className="text-white text-center text-sm max-w-lg font-light leading-relaxed mt-1">
                {filteredOngoingSites[ongoingLightboxIndex].details}
              </p>
            </div>

            {/* Next Image Trigger */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOngoingLightboxIndex((ongoingLightboxIndex + 1) % filteredOngoingSites.length);
              }}
              className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition-colors text-white z-10 cursor-pointer"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dynamic Bottom Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md border border-white/15 px-4 py-1.5 rounded-full text-xs font-poppins font-extrabold tracking-widest text-white shadow-md">
              {ongoingLightboxIndex + 1} OF {filteredOngoingSites.length} IMAGES
            </div>

          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );
}
